/**
 * Dialog <-> Intake Field Mapping Utilities
 * ----------------------------------------
 * This file is the single source of truth for mapping between dialog merge fields
 * and intake form data. If you add a new field to the mapping table below, it will
 * be used for BOTH dialog prefill (buildInitialMergeFields) and PATCHing the intake
 * (mapMergeFieldsToIntakePatch). This ensures strong typing, maintainability, and
 * prevents drift between the two directions.
 *
 * - buildInitialMergeFields: Intake form data → merge fields (for dialog prefill)
 * - mapMergeFieldsToIntakePatch: Merge fields (from dialog) → intake PATCH payload
 *
 * If you add a new dialog field, add it to the mapping table below.
 */
import { IntakeFormData as IntakeFormDataBackend } from '@/services/types/intake-types';

/**
 * Defines the mapping between dialog merge field keys and intake form data paths.
 * Add new fields here to keep both directions in sync.
 */
export type MergeFieldToIntakeMap = {
  mergeField: string;
  intakePath: string[]; // e.g., ['personal_info', 'email']
};

export const MERGE_FIELD_TO_INTAKE_MAP: MergeFieldToIntakeMap[] = [
  { mergeField: 'client_name', intakePath: ['personal_info', 'full_name'] },
  {
    mergeField: 'client_email_address',
    intakePath: ['personal_info', 'email'],
  },
  { mergeField: 'client_phone_number', intakePath: ['personal_info', 'phone'] },
  { mergeField: 'client_address', intakePath: ['personal_info', 'address'] },
  { mergeField: 'client_date_of_birth', intakePath: ['personal_info', 'dob'] },
  { mergeField: 'date_of_loss', intakePath: ['incident_info', 'date'] },
  {
    mergeField: 'accident_description',
    intakePath: ['incident_info', 'description'],
  },
  // Add more as needed
];

/**
 * Converts intake form data to a flat object of merge fields for dialog prefill.
 * Used to pre-populate the dialog with the latest intake values.
 *
 * @param formData IntakeFormData (backend-compatible)
 * @returns Record<string, string> of merge fields
 */
export function buildInitialMergeFields(
  formData: IntakeFormDataBackend,
): Record<string, string> {
  const result: Record<string, string> = {};
  MERGE_FIELD_TO_INTAKE_MAP.forEach(({ mergeField, intakePath }) => {
    const value = intakePath.reduce((acc, key) => acc?.[key], formData as any);
    if (typeof value === 'string' && value.trim() !== '') {
      result[mergeField] = value;
    }
  });
  return result;
}

/**
 * Maps dialog merge fields to a PATCH payload for the intake API.
 * Used to persist dialog values back to the intake before sending the document.
 *
 * @param mergeFields Record<string, string> from dialog
 * @param prevFormData IntakeFormData (backend-compatible)
 * @returns IntakeFormDataBackend suitable for PATCH
 */
export function mapMergeFieldsToIntakePatch(
  mergeFields: Record<string, string>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _prevFormData: IntakeFormDataBackend,
): Partial<IntakeFormDataBackend> {
  // Build a PARTIAL patch with only the fields from the merge field mapping.
  // Do NOT send the entire prevFormData — that overwrites any changes made
  // after the dialog opened (the old snapshot clobbers newer auto-saved values).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const patch: Record<string, any> = {};
  MERGE_FIELD_TO_INTAKE_MAP.forEach(({ mergeField, intakePath }) => {
    if (mergeFields[mergeField] !== undefined) {
      // Build nested path: e.g., ['personal_info', 'email'] → { personal_info: { email: value } }
      let current = patch;
      intakePath.slice(0, -1).forEach((key) => {
        if (!current[key]) current[key] = {};
        current = current[key];
      });
      current[intakePath[intakePath.length - 1]] = mergeFields[mergeField];
    }
  });
  return patch as Partial<IntakeFormDataBackend>;
}
